import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import { NodeEditor } from 'modules/workflows/components/NodeEditor';
import { getIsNodeEditorReadOnly, updateNodeData } from 'modules/workflows/ducks';
import { useDisplayCloneErrors } from 'modules/workflows/hooks/useDisplayCloneErrors';
import {
  ModifyChannelMembershipNodeData,
  ModifyMessageTypeMembershipNodeData,
} from 'modules/workflows/types';
import { InfoBanner } from 'ui/banner';
import { FormField } from 'ui/formField';

import { EditorBaseProps } from '../../types';
import ActionPicker from './ActionPicker';
import { getClonedField, getInfoMessage } from './ModifySubscriptionEditor.utils';
import SubscriptionPicker from './SubscriptionPicker';
import { EDITOR_TEST_ID, INFO_MESSAGE_ID } from './ModifySubscriptionEditor.constants';

type SubscriptionNodeData = ModifyChannelMembershipNodeData | ModifyMessageTypeMembershipNodeData;

export type ModifyListMembershipEditorProps = EditorBaseProps<SubscriptionNodeData>;

export const ModifySubscriptionEditor = ({
  nodeData: initialNodeData,
  nodeId,
  clonedFromNodeData,
  ...rest
}: ModifyListMembershipEditorProps) => {
  const dispatch = useDispatch();

  const isTileEditorReadOnly = useSelector(getIsNodeEditorReadOnly);
  const [nodeData, setNodeData] = useState(initialNodeData);
  const [errors, setErrors] = useState({ selectError: false, actionError: false });

  const isSubscriptionValid = useMemo(() => {
    return Number.isFinite(nodeData.channelId) || Number.isFinite(nodeData.messageTypeId);
  }, [nodeData.channelId, nodeData.messageTypeId, nodeData.nodeClass]);

  const clonedField = useMemo(
    () => getClonedField(initialNodeData?.nodeClass),
    [initialNodeData?.nodeClass],
  );

  const {
    isCloneWarningBannerShown,
    cloneErrors = {},
    cloneDisplayValues = {},
  } = useDisplayCloneErrors({
    clonedFromNodeData,
    fieldsToValidate: {
      [clonedField]: {
        isValid: isSubscriptionValid,
        displayValue: clonedFromNodeData?.resource?.name,
      },
    },
    nodeClass: nodeData.nodeClass,
  });

  const updateData = (data: Partial<SubscriptionNodeData>) => {
    const updatedData = { ...nodeData, ...data };
    setErrors({
      selectError:
        !updatedData.channelId &&
        isEmpty(updatedData.channelIds) &&
        !updatedData.messageTypeId &&
        isEmpty(updatedData.messageTypeIds),
      actionError: typeof updatedData.subscribed !== 'boolean',
    });
    setNodeData(updatedData);
  };

  const handleUpdate = (requiresValidation = true) => {
    // If you need valid data to update and if there are errors,
    // do not allow updating
    if (requiresValidation && Object.values(errors).includes(true)) return;

    dispatch(updateNodeData(nodeData as Partial<SubscriptionNodeData>, nodeId));
  };

  return (
    <NodeEditor
      {...rest}
      dataTest={EDITOR_TEST_ID}
      onUpdate={handleUpdate}
      /** These are used to check if node has changes */
      initialNodeData={initialNodeData}
      updatedNodeData={nodeData}
      isCloneWarningBannerShown={isCloneWarningBannerShown}
    >
      {/* Picks if the user is subscribing or unsubscribing */}
      <ActionPicker
        errorText="Please select an action."
        hasError={errors.actionError}
        isDisabled={isTileEditorReadOnly}
        value={nodeData.subscribed}
        onSelect={(subscribed) => updateData({ ...nodeData, subscribed })}
      />

      <SubscriptionPicker
        placeholder={cloneDisplayValues[clonedField] || 'Select one..'}
        errorText={errors.selectError ? 'Please select a channel or message type.' : ''}
        hasError={cloneErrors[clonedField] || errors.selectError}
        isDisabled={isTileEditorReadOnly}
        nodeData={nodeData}
        onSelect={(data) => updateData({ ...nodeData, ...data })}
      />

      {/* Display info message only if there is one to display */}
      {isSubscriptionValid && (
        <FormField>
          <InfoBanner data-test={INFO_MESSAGE_ID}>
            {getInfoMessage(nodeData.subscribed, nodeData.nodeClass)}
          </InfoBanner>
        </FormField>
      )}
    </NodeEditor>
  );
};
